// src/pages/pmc/PmcBoqSetupPane.js

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function inr(v) {
  return `₹${Number(v || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
}

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    x.city ||
    ""
  );
}

function isStrictNafed(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeToProject(project, nafedScope) {
  if (!nafedScope?.onlyNafedApply && !nafedScope?.strictNafedApplyOnly) {
    return true;
  }

  const scopeState = clean(nafedScope?.state);

  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const stateOk = !scopeState || clean(getState(project)) === scopeState;

  const districtOk =
    !allowedDistricts.length ||
    allowedDistricts.includes(clean(getDistrict(project)));

  return isStrictNafed(project) && stateOk && districtOk;
}

function tenderMatchesAssignedProject(tender, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function StatCard({ label, value, note }) {
  return (
    <div className="boq-stat-card">
      <label>{label}</label>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

function Badge({ type = "pending", children }) {
  return <span className={`boq-badge ${type}`}>{children}</span>;
}

function exportStatusCsv(rows) {
  const headers = [
    "Sr",
    "Project / Tender ID",
    "PACS",
    "Project",
    "Location",
    "Contractor",
    "Status",
    "Items",
    "BOQ Value",
  ];

  const data = rows.map((r, i) => [
    i + 1,
    r.id,
    r.pacsName,
    r.title,
    r.location,
    r.awardedVendorName,
    r.status,
    r.itemCount,
    r.totalAmount,
  ]);

  const csv = [headers, ...data]
    .map((row) => row.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "NAFED_BOQ_Status_Report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function exportStatusPdf(rows) {
  const pdf = new jsPDF("l", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  let y = 42;

  pdf.setFillColor(6, 43, 85);
  pdf.rect(0, 0, pageWidth, 55, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text("NAFED BOQ Upload Status Report", 40, 34);

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(8);
  y = 80;

  const headers = ["Sr", "PACS", "Project", "Location", "Contractor", "Status", "Items", "BOQ Value"];
  const widths = [28, 150, 180, 95, 145, 75, 45, 90];

  const drawHeader = () => {
    let x = 28;
    pdf.setFillColor(226, 232, 240);
    pdf.setFont("helvetica", "bold");

    headers.forEach((h, i) => {
      pdf.rect(x, y, widths[i], 24, "FD");
      pdf.text(h, x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
    pdf.setFont("helvetica", "normal");
  };

  drawHeader();

  rows.forEach((r, idx) => {
    if (y > 545) {
      pdf.addPage();
      y = 40;
      drawHeader();
    }

    const values = [
      idx + 1,
      r.pacsName || "—",
      r.title || "—",
      r.location || "—",
      r.awardedVendorName || "—",
      r.status || "—",
      r.itemCount || 0,
      inr(r.totalAmount || 0),
    ];

    let x = 28;

    values.forEach((v, i) => {
      pdf.rect(x, y, widths[i], 24);
      pdf.text(String(v).slice(0, i === 2 ? 28 : 18), x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
  });

  pdf.save("NAFED_BOQ_Status_Report.pdf");
}

export default function PmcBoqSetupPane({ nafedScope }) {
  const [tenders, setTenders] = useState([]);
  const [loadingTenders, setLoadingTenders] = useState(true);

  const [selectedTenderId, setSelectedTenderId] = useState("");
  const [tenderId, setTenderId] = useState("");
  const [selectedTender, setSelectedTender] = useState(null);

  const [existingBoq, setExistingBoq] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);

  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [overwrite, setOverwrite] = useState(true);

  const [boqStatusRows, setBoqStatusRows] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusSearch, setStatusSearch] = useState("");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  const loadExistingBoqForTender = async (tid) => {
    if (!tid) {
      setExistingBoq([]);
      return [];
    }

    try {
      setLoadingExisting(true);

      const snap = await getDocs(collection(db, "tenders", tid, "boq_items"));

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      list.sort((a, b) => Number(a.itemNo || 0) - Number(b.itemNo || 0));

      setExistingBoq(list);
      return list;
    } catch (e) {
      console.error(e);
      toast.error("Existing BOQ load failed.");
      return [];
    } finally {
      setLoadingExisting(false);
    }
  };

  const getNafedAssignedProjects = async () => {
    const snap = await getDocs(collection(db, "godown_projects"));

    const assignedProjects = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => applyNafedScopeToProject(p, nafedScope));

    const projectIds = new Set(assignedProjects.map((p) => String(p.id)));

    const applicationIds = new Set(
      assignedProjects
        .map((p) => String(p.applicationId || ""))
        .filter(Boolean)
    );

    return { assignedProjects, projectIds, applicationIds };
  };

  const loadBoqStatusForAll = async (awardedList) => {
    try {
      setLoadingStatus(true);

      const statusList = [];

      for (const t of awardedList) {
        const boqSnap = await getDocs(collection(db, "tenders", t.id, "boq_items"));

        let totalQty = 0;
        let totalAmount = 0;

        boqSnap.forEach((d) => {
          const item = d.data();
          const qty = Number(item.boqQty || item.qtyTender || item.quantity || 0);
          const rate = Number(item.rateWithLC || item.rate || 0);
          totalQty += qty;
          totalAmount += qty * rate;
        });

        const itemCount = boqSnap.size;

        statusList.push({
          id: t.id,
          title: t.title || t.projectName || t.workName || "Untitled Project",
          pacsName:
            t.ownerCompany ||
            t.pacsName ||
            t.pacsShortName ||
            t.societyName ||
            "—",
          location:
            t.city ||
            t.district ||
            t.projectDistrict ||
            t.state ||
            "—",
          awardedVendorUid: t.awardedVendorUid || "",
          awardedVendorName:
            t.awardedVendorName ||
            t.vendorName ||
            t.contractorName ||
            "—",
          itemCount,
          totalQty,
          totalAmount,
          status: itemCount > 0 ? "UPLOADED" : "PENDING",
          raw: t,
        });
      }

      statusList.sort((a, b) => {
        if (a.status !== b.status) return a.status === "PENDING" ? -1 : 1;
        return String(a.pacsName || "").localeCompare(String(b.pacsName || ""));
      });

      setBoqStatusRows(statusList);
    } catch (e) {
      console.error(e);
      toast.error("BOQ status loading failed.");
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadTenders = async () => {
    try {
      setLoadingTenders(true);

      const snap = await getDocs(collection(db, "tenders"));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      list = list.filter((t) => t.awardedVendorUid);

      if (isNafedMode) {
        const { projectIds, applicationIds } = await getNafedAssignedProjects();

        const scopeState = clean(nafedScope?.state);
        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        list = list.filter((t) => {
          const linked = tenderMatchesAssignedProject(t, projectIds, applicationIds);

          const tenderStrict =
            t.nafedProject === true &&
            t.projectSource === "NAFED";

          const stateOk =
            !scopeState || clean(getState(t)) === scopeState;

          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(t)));

          return linked || (tenderStrict && stateOk && districtOk);
        });
      }

      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setTenders(list);
      await loadBoqStatusForAll(list);
    } catch (e) {
      console.error(e);
      toast.error("Tenders load failed.");
    } finally {
      setLoadingTenders(false);
    }
  };

  useEffect(() => {
    loadTenders();
  }, [JSON.stringify(nafedScope || {})]);

  const summary = useMemo(() => {
    const total = boqStatusRows.length;
    const uploaded = boqStatusRows.filter((x) => x.status === "UPLOADED").length;
    const pending = boqStatusRows.filter((x) => x.status === "PENDING").length;
    const totalItems = boqStatusRows.reduce(
      (sum, x) => sum + Number(x.itemCount || 0),
      0
    );
    const totalValue = boqStatusRows.reduce(
      (sum, x) => sum + Number(x.totalAmount || 0),
      0
    );

    return { total, uploaded, pending, totalItems, totalValue };
  }, [boqStatusRows]);

  const filteredStatusRows = useMemo(() => {
    let list = [...boqStatusRows];

    if (statusFilter !== "all") {
      list = list.filter((x) => x.status === statusFilter);
    }

    const needle = statusSearch.trim().toLowerCase();

    if (needle) {
      list = list.filter((x) => {
        const hay = [
          x.title,
          x.pacsName,
          x.location,
          x.awardedVendorName,
          x.id,
        ]
          .map((v) => String(v || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [boqStatusRows, statusFilter, statusSearch]);

  const handleSelectTender = async (id) => {
    setSelectedTenderId(id);
    setTenderId(id);
    setRows([]);

    const t = tenders.find((x) => x.id === id) || null;
    setSelectedTender(t);

    if (id) {
      await loadExistingBoqForTender(id);
    } else {
      setExistingBoq([]);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    try {
      setSaving(true);

      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];

      const json = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
        raw: true,
      });

      const parsed = json
        .map((row, index) => {
          const itemNo = Number(
            row["Item No."] ||
              row["Item No"] ||
              row["ITEM NO"] ||
              row["Item"] ||
              index + 1
          );

          const description = String(
            row["Item Description"] ||
              row["Description"] ||
              row["DESCRIPTION"] ||
              ""
          ).trim();

          if (!itemNo || !description) return null;

          return {
            itemNo,
            description,
            unit: String(row["Unit"] || row["UNIT"] || "").trim(),
            boqQty: Number(
              row["Qty. Estimated"] ||
                row["Quantity"] ||
                row["Qty"] ||
                0
            ),
            rate: Number(row["Rate (Rs.)"] || row["Rate"] || 0),
            rateWithLC: Number(
              row["Rate with 1% LC"] ||
                row["Rate with LC"] ||
                row["Rate + LC"] ||
                row["Rate"] ||
                0
            ),
          };
        })
        .filter(Boolean);

      if (!parsed.length) {
        toast.error("Excel me valid BOQ rows nahi mile.");
        setRows([]);
        return;
      }

      setRows(parsed);
      toast.success(`${parsed.length} BOQ items loaded.`);
    } catch (err) {
      console.error(err);
      toast.error("Excel read failed.");
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    const tid = tenderId.trim();

    if (!tid) {
      toast.error("Pehle project/tender select karo.");
      return;
    }

    if (!rows.length) {
      toast.error("Pehle Excel upload karo.");
      return;
    }

    try {
      setSaving(true);

      const colRef = collection(db, "tenders", tid, "boq_items");

      if (overwrite) {
        const snap = await getDocs(colRef);
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      }

      const writes = rows.map((row) => {
        const id = String(row.itemNo).padStart(3, "0");
        return setDoc(
          doc(colRef, id),
          {
            ...row,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      await Promise.all(writes);

      toast.success("BOQ saved successfully.");

      await loadExistingBoqForTender(tid);
      await loadTenders();

      setRows([]);
    } catch (err) {
      console.error(err);
      toast.error("BOQ save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExisting = async () => {
    if (!selectedTenderId) return;

    if (!existingBoq.length) {
      toast.error("Is tender ke liye BOQ saved nahi hai.");
      return;
    }

    const ok = window.confirm(
      "Selected project ka pura BOQ delete karna hai? Ye action reversible nahi hai."
    );

    if (!ok) return;

    try {
      setSaving(true);

      const colRef = collection(db, "tenders", selectedTenderId, "boq_items");
      const snap = await getDocs(colRef);

      await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));

      setExistingBoq([]);

      toast.success("Existing BOQ deleted.");
      await loadTenders();
    } catch (e) {
      console.error(e);
      toast.error("BOQ delete failed.");
    } finally {
      setSaving(false);
    }
  };

  const quickSelectFromTable = async (row) => {
    await handleSelectTender(row.id);

    setTimeout(() => {
      const el = document.getElementById("boq-upload-panel");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const previewAmount = useMemo(() => {
    return rows.reduce(
      (sum, r) => sum + Number(r.boqQty || 0) * Number(r.rateWithLC || r.rate || 0),
      0
    );
  }, [rows]);

  const existingAmount = useMemo(() => {
    return existingBoq.reduce(
      (sum, r) => sum + Number(r.boqQty || 0) * Number(r.rateWithLC || r.rate || 0),
      0
    );
  }, [existingBoq]);

  return (
    <div className="boq-page">
      <style>{`
        .boq-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .boq-hero {
          background: linear-gradient(135deg, #062b55, #064e3b);
          color: white;
          border-radius: 22px;
          padding: 24px;
          box-shadow: 0 18px 40px rgba(6,43,85,.20);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
        }

        .boq-hero h2 {
          margin: 0;
          font-size: 26px;
          font-weight: 950;
        }

        .boq-hero p {
          margin: 8px 0 0;
          color: #d1fae5;
          font-size: 13px;
          line-height: 1.7;
          max-width: 760px;
        }

        .boq-hero small {
          display: inline-block;
          color: #bbf7d0;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .18em;
        }

        .boq-btn {
          border: 0;
          border-radius: 11px;
          padding: 10px 14px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          white-space: nowrap;
        }

        .boq-btn.white {
          background: white;
          color: #064e3b;
        }

        .boq-btn.primary {
          background: #062b55;
          color: white;
        }

        .boq-btn.green {
          background: #16a34a;
          color: white;
        }

        .boq-btn.red {
          background: #ef4444;
          color: white;
        }

        .boq-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .boq-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .boq-stats {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 12px;
        }

        .boq-stat-card {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .boq-stat-card label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .boq-stat-card b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .boq-stat-card small {
          display: block;
          margin-top: 5px;
          color: #64748b;
          font-size: 11px;
          font-weight: 750;
        }

        .boq-panel {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .boq-panel-head {
          padding: 15px 18px;
          background: #f8fafc;
          border-bottom: 1.5px solid #111827;
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        }

        .boq-panel-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .boq-panel-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }

        .boq-filter {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .boq-input {
          height: 38px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          background: white;
          color: #071f45;
          outline: none;
        }

        .boq-table-wrap {
          overflow-x: auto;
        }

        .boq-table {
          width: 100%;
          min-width: 1200px;
          border-collapse: collapse;
          font-size: 12px;
        }

        .boq-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 12px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .boq-table td {
          padding: 12px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
        }

        .boq-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .boq-table tbody tr:hover td {
          background: #eef6ff;
        }

        .boq-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .boq-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .boq-badge.uploaded {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .boq-badge.pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .boq-upload-grid {
          display: grid;
          grid-template-columns: 1.4fr .8fr;
          gap: 18px;
          padding: 18px;
        }

        .boq-box {
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          background: #f8fafc;
          padding: 16px;
        }

        .boq-box h4 {
          margin: 0 0 12px;
          color: #071f45;
          font-size: 15px;
          font-weight: 950;
        }

        .boq-field {
          display: grid;
          gap: 6px;
          margin-bottom: 12px;
        }

        .boq-field label {
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .boq-field select,
        .boq-field input {
          width: 100%;
          height: 40px;
          border: 1px solid #cbd5e1;
          border-radius: 11px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          outline: none;
          background: white;
        }

        .selected-card {
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 14px;
          padding: 14px;
          font-size: 12px;
          line-height: 1.8;
          color: #334155;
        }

        .file-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          flex-wrap: wrap;
        }

        .empty {
          padding: 35px;
          text-align: center;
          color: #64748b;
          font-weight: 850;
        }

        @media(max-width:1300px) {
          .boq-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .boq-upload-grid {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width:700px) {
          .boq-stats {
            grid-template-columns: 1fr;
          }

          .boq-hero,
          .boq-panel-head {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <section className="boq-hero">
        <div>
          <small>BOQ & RA Automation</small>
          <h2>BOQ Upload Control Room</h2>
          <p>
            Awarded project-wise BOQ upload, replacement, verification and RA
            bill base setup. In NAFED PMC mode, this page shows only projects
            within this PMC&apos;s allocated state/district scope.
          </p>
        </div>

        <button type="button" onClick={loadTenders} className="boq-btn white">
          Refresh Status
        </button>
      </section>

      <section className="boq-stats">
        <StatCard label="Awarded Projects" value={summary.total} note="Scoped awarded tenders" />
        <StatCard label="BOQ Uploaded" value={summary.uploaded} note="Ready for RA billing" />
        <StatCard label="BOQ Pending" value={summary.pending} note="Upload required" />
        <StatCard label="Total BOQ Items" value={summary.totalItems} note="Saved item count" />
        <StatCard label="Total BOQ Value" value={inr(summary.totalValue)} note="Approx value" />
      </section>

      <section className="boq-panel">
        <div className="boq-panel-head">
          <div>
            <h3>Project-wise BOQ Upload Status</h3>
            <p>Black-line professional table with filtered NAFED project scope.</p>
          </div>

          <div className="boq-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="boq-input"
            >
              <option value="all">All Status</option>
              <option value="UPLOADED">Uploaded</option>
              <option value="PENDING">Pending</option>
            </select>

            <input
              value={statusSearch}
              onChange={(e) => setStatusSearch(e.target.value)}
              placeholder="Search PACS / project / vendor..."
              className="boq-input"
            />

            <button className="boq-btn green" onClick={() => exportStatusCsv(filteredStatusRows)}>
              Excel / CSV
            </button>

            <button className="boq-btn primary" onClick={() => exportStatusPdf(filteredStatusRows)}>
              PDF
            </button>
          </div>
        </div>

        {loadingStatus || loadingTenders ? (
          <div className="empty">BOQ status loading...</div>
        ) : filteredStatusRows.length === 0 ? (
          <div className="empty">No awarded project found in current scope.</div>
        ) : (
          <div className="boq-table-wrap">
            <table className="boq-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>PACS / Project</th>
                  <th>Location</th>
                  <th>Contractor</th>
                  <th>Status</th>
                  <th>Items</th>
                  <th>BOQ Value</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredStatusRows.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>

                    <td>
                      <div className="boq-title">{r.pacsName}</div>
                      <div className="muted">{r.title}</div>
                      <div className="muted">ID: {r.id}</div>
                    </td>

                    <td>{r.location}</td>

                    <td>
                      <div className="boq-title">{r.awardedVendorName}</div>
                      <div className="muted">{r.awardedVendorUid || "—"}</div>
                    </td>

                    <td>
                      {r.status === "UPLOADED" ? (
                        <Badge type="uploaded">UPLOADED</Badge>
                      ) : (
                        <Badge type="pending">PENDING</Badge>
                      )}
                    </td>

                    <td style={{ textAlign: "right" }}>{r.itemCount}</td>

                    <td style={{ textAlign: "right" }}>
                      {inr(r.totalAmount)}
                    </td>

                    <td>
                      <button
                        type="button"
                        onClick={() => quickSelectFromTable(r)}
                        className={cx(
                          "boq-btn",
                          r.status === "UPLOADED" ? "primary" : "green"
                        )}
                      >
                        {r.status === "UPLOADED" ? "View / Replace" : "Upload Now"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="boq-upload-panel" className="boq-panel">
        <div className="boq-panel-head">
          <div>
            <h3>Upload / Replace BOQ</h3>
            <p>Upload Excel BOQ for selected awarded project.</p>
          </div>
        </div>

        <div className="boq-upload-grid">
          <div className="boq-box">
            <h4>Project Selection</h4>

            <div className="boq-field">
              <label>Select Awarded Project / Tender</label>

              <select
                value={selectedTenderId}
                onChange={(e) => handleSelectTender(e.target.value)}
                disabled={loadingTenders}
              >
                <option value="">— Select Awarded Project —</option>

                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.ownerCompany || t.pacsName || "PACS") +
                      " · " +
                      (t.title || t.projectName || "Untitled Project") +
                      " · " +
                      (t.city || t.district || "")}
                  </option>
                ))}
              </select>
            </div>

            <div className="boq-field">
              <label>Tender ID</label>
              <input
                value={tenderId}
                onChange={(e) => setTenderId(e.target.value)}
                placeholder="Tender ID"
              />
            </div>

            {selectedTender && (
              <div className="selected-card">
                <b>PACS:</b>{" "}
                {selectedTender.ownerCompany || selectedTender.pacsName || "—"}
                <br />
                <b>Title:</b>{" "}
                {selectedTender.title || selectedTender.projectName || "—"}
                <br />
                <b>Location:</b>{" "}
                {selectedTender.city || selectedTender.district || "—"}
                <br />
                <b>Awarded Vendor:</b>{" "}
                {selectedTender.awardedVendorName ||
                  selectedTender.vendorName ||
                  "—"}
              </div>
            )}
          </div>

          <div className="boq-box">
            <h4>Excel Upload</h4>

            <div className="boq-field">
              <label>Upload BOQ Excel</label>
              <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
            </div>

            <div className="muted">
              Required columns: Item No., Item Description, Unit, Qty.
              Estimated, Rate, Rate with 1% LC.
            </div>

            <div className="file-row" style={{ marginTop: 14 }}>
              <label className="muted" style={{ display: "flex", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite(e.target.checked)}
                />
                Existing BOQ overwrite
              </label>

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !rows.length || !tenderId.trim()}
                className="boq-btn green"
              >
                {saving ? "Saving..." : "Save BOQ"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="boq-panel">
        <div className="boq-panel-head">
          <div>
            <h3>BOQ Preview from Excel</h3>
            <p>
              Items: <b>{rows.length}</b> | Value: <b>{inr(previewAmount)}</b>
            </p>
          </div>
        </div>

        {!rows.length ? (
          <div className="empty">Excel upload karne ke baad preview dikhega.</div>
        ) : (
          <div className="boq-table-wrap">
            <table className="boq-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Unit</th>
                  <th>BOQ Qty</th>
                  <th>Rate</th>
                  <th>Rate + LC</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((r) => (
                  <tr key={r.itemNo}>
                    <td>{r.itemNo}</td>
                    <td>{r.description}</td>
                    <td>{r.unit}</td>
                    <td style={{ textAlign: "right" }}>{r.boqQty}</td>
                    <td style={{ textAlign: "right" }}>{inr(r.rate)}</td>
                    <td style={{ textAlign: "right" }}>{inr(r.rateWithLC)}</td>
                    <td style={{ textAlign: "right" }}>
                      {inr(Number(r.boqQty || 0) * Number(r.rateWithLC || r.rate || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="boq-panel">
        <div className="boq-panel-head">
          <div>
            <h3>Existing BOQ Saved in System</h3>
            <p>
              Items: <b>{existingBoq.length}</b> | Value:{" "}
              <b>{inr(existingAmount)}</b>
            </p>
          </div>

          <button
            type="button"
            onClick={handleDeleteExisting}
            disabled={!selectedTenderId || !existingBoq.length || saving}
            className="boq-btn red"
          >
            Delete Existing BOQ
          </button>
        </div>

        {!selectedTenderId ? (
          <div className="empty">Pehle project select karein.</div>
        ) : loadingExisting ? (
          <div className="empty">Existing BOQ loading...</div>
        ) : !existingBoq.length ? (
          <div className="empty">Selected project ke liye BOQ saved nahi hai.</div>
        ) : (
          <div className="boq-table-wrap">
            <table className="boq-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Unit</th>
                  <th>BOQ Qty</th>
                  <th>Rate</th>
                  <th>Rate + LC</th>
                  <th>Amount</th>
                </tr>
              </thead>

              <tbody>
                {existingBoq.map((r) => (
                  <tr key={r.id}>
                    <td>{r.itemNo}</td>
                    <td>{r.description}</td>
                    <td>{r.unit}</td>
                    <td style={{ textAlign: "right" }}>{r.boqQty}</td>
                    <td style={{ textAlign: "right" }}>{inr(r.rate)}</td>
                    <td style={{ textAlign: "right" }}>{inr(r.rateWithLC)}</td>
                    <td style={{ textAlign: "right" }}>
                      {inr(Number(r.boqQty || 0) * Number(r.rateWithLC || r.rate || 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}